'use client';

import React from 'react';
import clsx from 'clsx';
import { useVirtualizedGridContainer } from './VirtualizedGridContainer.hooks';
import styles from './VirtualizedGridContainer.module.scss';
import VirtualizedGridContainerConsts from './VirtualizedGridContainer.settings';
import type { VirtualizedGridContainerProps } from './VirtualizedGridContainer.types';
import { useQueryClient } from 'react-query';
import { CartProps, EQuerykeys, ECart } from '../../../models/index';

const { DEFAULT_GAP } = VirtualizedGridContainerConsts;

export const VirtualizedGridContainer = React.memo(
	({
		children,
		gap = DEFAULT_GAP,
		estimatedItemHeight,
		overscan,
		className,
		gridBreakpoints,
		loadMoreContent,
		enableColumnTransition,
	}: VirtualizedGridContainerProps) => {
		const normalizedChildren = React.useMemo(
			() => React.Children.toArray(children).filter(React.isValidElement),
			[children]
		);

		const count = normalizedChildren.length;

		const { parentRef, virtualizer, columnCount, isResizing } = useVirtualizedGridContainer({
			count,
			gridBreakpoints,
			estimatedItemHeight,
			gap,
			overscan,
			enableColumnTransition,
		});

		const queryClient = useQueryClient();

		const activeCartData: CartProps | undefined = queryClient.getQueryData([EQuerykeys.cart, ECart.active]);

		const hasActiveCartItems = (activeCartData?.products ?? [])?.length > 0;

		return (
			<div
				ref={parentRef}
				className={clsx(
					'w-full overflow-y-auto md:scrollbar-auto scrollbar-hide',
					className,
					enableColumnTransition && isResizing && styles.transitionColumns,
					hasActiveCartItems ? 'h-[calc(100vh_-_350px)] md:h-[60vh]' : 'h-[calc(100vh_-200px)] md:h-[60vh]'
				)}
				style={
					{
						'--grid-columns': columnCount,
						'--grid-gap': `${gap}px`,
					} as React.CSSProperties
				}
			>
				<div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
					{virtualizer.getVirtualItems().map((v) => {
						const child = normalizedChildren[v.index];
						if (!child) return;

						return (
							<div
								key={v.key}
								ref={virtualizer.measureElement}
								className={styles.gridItem}
								style={
									{
										'--grid-lane': v.lane,
										'--grid-item-start': `${v.start}px`,
									} as React.CSSProperties
								}
							>
								{child}
							</div>
						);
					})}
				</div>

				{loadMoreContent}
			</div>
		);
	}
);
